import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { MapPinned, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

export function CollectivesQueue() {
  const [items, setItems] = useState([]);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    api.adminCollectives("pending").then(setItems).catch(() => setItems([]));
  }, []);

  const approve = async (c) => {
    try {
      await api.approveCollective(c.id);
      setItems((list) => list.filter(x => x.id !== c.id));
      toast.success(`${c.name} is live — the founder is now a Community Leader`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
  };

  const reject = async () => {
    try {
      await api.rejectCollective(rejecting.id, reason);
      setItems((list) => list.filter(x => x.id !== rejecting.id));
      toast("Application rejected — the applicant has been notified");
      setRejecting(null); setReason("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reject"); }
  };

  return (
    <div className="mt-14" data-testid="admin-collectives-section">
      <h2 className="font-display text-2xl text-forest flex items-center gap-2">
        <MapPinned className="w-5 h-5 text-terracotta" /> Collective applications
      </h2>
      <p className="text-neutral-600 text-sm mt-1">People applying to become Community Leaders for their area.</p>
      <div className="mt-5 space-y-4">
        {items.length === 0 && <div className="text-sm text-neutral-500">No pending applications.</div>}
        {items.map((c) => (
          <Card key={c.id} className="border-neutral-200" data-testid={`collective-app-${c.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-display text-xl text-forest">{c.name}</div>
                  <div className="text-sm text-neutral-600 mt-0.5">{c.area_label} · {c.radius_miles} mile radius</div>
                  <div className="text-sm text-neutral-700 mt-2">
                    Applicant: <span className="font-medium">{c.founder_name}</span>
                  </div>
                  <p className="text-sm text-neutral-700 mt-2 max-w-xl">{c.description}</p>
                  <div className="text-sm text-neutral-600 mt-2 italic bg-forest/5 border border-forest/10 rounded-lg px-3 py-2">
                    "{c.motivation}"
                  </div>
                  {c.nearby?.length > 0 && (
                    <div data-testid={`admin-nearby-${c.id}`} className="mt-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <span className="font-medium">
                        {c.nearby.length} active collective{c.nearby.length !== 1 ? "s" : ""} within 3 miles:
                      </span>{" "}
                      {c.nearby.map((n) => `${n.name} (${n.distance_miles} mi, ${n.member_count} member${n.member_count !== 1 ? "s" : ""})`).join("; ")}
                    </div>
                  )}
                </div>
                <Badge className="bg-amber-100 text-amber-800 border-none inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Pending
                </Badge>
              </div>
              <div className="flex gap-2 mt-4">
                <Button data-testid={`collective-approve-${c.id}`} onClick={() => approve(c)} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-4 text-sm">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                </Button>
                <Button data-testid={`collective-reject-${c.id}`} onClick={() => setRejecting(c)} variant="outline" className="rounded-full h-9 px-4 text-sm">
                  <XCircle className="w-4 h-4 mr-1.5" /> Reject
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent data-testid="collective-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject "{rejecting?.name}"?</DialogTitle>
            <DialogDescription>The applicant will be notified with your reason and can apply again.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="collective-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason (optional but kind)" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button data-testid="collective-reject-confirm" onClick={reject} className="rounded-full bg-red-600 hover:bg-red-700">Reject & notify</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
